/**
 * ClickHub - Serviço de Notificações Push
 * Gerencia a inscrição e recebimento de notificações push no navegador
 */

import authService from "./authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class NotificationService {
  constructor() {
    this.swRegistration = null;
    this.vapidPublicKey = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Verifica se o navegador suporta notificações push
   */
  checkSupport() {
    if (!this.isSupported) {
      console.warn('Push notifications não suportadas neste navegador');
      return false;
    }
    return true;
  }

  /**
   * Obtém a chave pública VAPID do servidor
   */
  async getVapidPublicKey() {
    if (this.vapidPublicKey) return this.vapidPublicKey;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/vapid-public-key`);
      if (!response.ok) throw new Error('Erro ao obter chave VAPID');
      const data = await response.json();
      this.vapidPublicKey = data.public_key;
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Erro ao obter chave VAPID:', error);
      throw error;
    }
  }

  /**
   * Registra o Service Worker para push notifications
   */
  async registerServiceWorker() {
    if (!this.checkSupport()) return null;
    
    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw-push.js');
      console.log('✅ Service Worker registrado:', this.swRegistration);
      return this.swRegistration;
    } catch (error) {
      console.error('Erro ao registrar Service Worker:', error);
      throw error;
    }
  }

  /**
   * Solicita permissão para enviar notificações
   */
  async requestPermission() {
    if (!this.checkSupport()) return 'denied';
    
    const permission = await Notification.requestPermission();
    console.log('Permissão de notificação:', permission);
    return permission;
  }

  /**
   * Verifica o status atual da permissão
   */
  getPermissionStatus() {
    if (!this.checkSupport()) return 'denied';
    return Notification.permission;
  }

  /**
   * Converte chave VAPID de base64 para Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Inscreve o usuário para receber notificações push
   * @param {string} usuarioId - ID do usuário
   * @param {string} tipoUsuario - Tipo: 'admin', 'fornecedor' ou 'afiliado'
   */
  async subscribe(usuarioId, tipoUsuario) {
    if (!this.checkSupport()) {
      throw new Error('Push notifications não suportadas');
    }

    // Verificar permissão
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Permissão de notificação negada');
    }

    // Registrar Service Worker se necessário
    if (!this.swRegistration) {
      await this.registerServiceWorker();
    }

    // Aguardar Service Worker estar pronto
    const registration = await navigator.serviceWorker.ready;

    // Obter chave VAPID
    const vapidKey = await this.getVapidPublicKey();
    const convertedKey = this.urlBase64ToUint8Array(vapidKey);

    // Criar subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertedKey
    });

    console.log('📱 Subscription criada:', subscription);

    // Enviar subscription para o servidor
    const response = await authService.fetchAuth(`${BACKEND_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        usuario_id: usuarioId,
        tipo_usuario: tipoUsuario,
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
          }
        }
      })
    });

    if (!response.ok) {
      throw new Error('Erro ao registrar subscription no servidor');
    }

    const result = await response.json();
    console.log('✅ Inscrito para notificações:', result);
    return result;
  }

  /**
   * Cancela a inscrição de notificações push
   */
  async unsubscribe() {
    if (!this.swRegistration) return;

    const subscription = await this.swRegistration.pushManager.getSubscription();
    
    if (subscription) {
      // Cancelar no navegador
      await subscription.unsubscribe();
      
      // Cancelar no servidor
      await authService.fetchAuth(`${BACKEND_URL}/api/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'POST'
      });
      
      console.log('🔕 Inscrição cancelada');
    }
  }

  /**
   * Verifica se o usuário está inscrito
   */
  async isSubscribed() {
    if (!this.checkSupport()) return false;
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  }

  /**
   * Envia uma notificação de teste
   * @param {string} usuarioId - ID do usuário
   * @param {string} tipoUsuario - Tipo do usuário
   */
  async sendTest(usuarioId, tipoUsuario) {
    const response = await authService.fetchAuth(`${BACKEND_URL}/api/notifications/test/${usuarioId}/${tipoUsuario}`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('Erro ao enviar notificação de teste');
    }
    
    return await response.json();
  }

  /**
   * Obtém estatísticas de notificações
   */
  async getStats() {
    const response = await authService.fetchAuth(`${BACKEND_URL}/api/notifications/stats`);
    
    if (!response.ok) {
      throw new Error('Erro ao obter estatísticas');
    }
    
    return await response.json();
  }

  /**
   * Mostra uma notificação local (sem push)
   * @param {string} title - Título
   * @param {object} options - Opções da notificação
   */
  showLocalNotification(title, options = {}) {
    if (Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options
      });
    }
  }
}

// Exportar instância única
const notificationService = new NotificationService();
export default notificationService;
